import { arrayToEntityDict, type Entity, type EntityDict } from '@webapp2/shared/model'
import { capitalize } from 'vue'

let db: IDBDatabase | null = null
const dbName = 'OssoVR'
const adminStore = 'admin'
const organizationStore = 'organization'
const version = 7
const initTimeoutMs = 3000

const init = new Promise<void>(resolve => {
  const request = indexedDB.open(dbName, version)
  let abandoned = false
  const timeout = setTimeout(() => {
    console.error('Cache init timeout')
    abandoned = true
    resolve()
  }, initTimeoutMs)

  request.onsuccess = () => {
    clearTimeout(timeout)
    if (abandoned) {
      request.result.close()
      return
    }
    db = request.result
    db.onversionchange = () => {
      db?.close()
      db = null
    }
    resolve()
  }
  request.onerror = () => {
    clearTimeout(timeout)
    console.error('Cache init failure', request.error)
    resolve()
  }
  request.onblocked = () => {
    clearTimeout(timeout)
    console.error('Cache init blocked')
    abandoned = true
    resolve()
  }

  request.onupgradeneeded = event => {
    const database = (event.target as IDBOpenDBRequest).result
    for (const name of Array.from(database.objectStoreNames)) {
      database.deleteObjectStore(name)
    }
    const orgStore = database.createObjectStore(organizationStore)
    orgStore.createIndex('type', 'type', { unique: false })
    const admStore = database.createObjectStore(adminStore)
    admStore.createIndex('type', 'type', { unique: false })
  }
})

function getTransaction(name: string, mode: 'readonly' | 'readwrite'): IDBTransaction | null {
  if (!db) {
    return null
  }
  try {
    return db.transaction([name], mode)
  } catch (error) {
    console.error('Cache transaction failure', error)
    return null
  }
}

function getStore(name: string, mode: 'readonly' | 'readwrite'): IDBObjectStore | null {
  const transaction = getTransaction(name, mode)
  return transaction ? transaction.objectStore(name) : null
}

export class Cache {
  constructor(private name: string) {}

  async get<T>(id: string): Promise<T | undefined> {
    await init
    const store = getStore(this.name, 'readonly')
    if (!store) {
      return undefined
    }
    return new Promise(resolve => {
      const request = store.get(id) as IDBRequest<T>
      request.onsuccess = () => resolve(request.result || undefined)
      request.onerror = () => {
        console.error(`Cache get failure ${id}`, request.error)
        resolve(undefined)
      }
    })
  }

  async set<T>(id: string, value: T): Promise<void> {
    await init
    const store = getStore(this.name, 'readwrite')
    if (!store) {
      return
    }

    return new Promise(resolve => {
      const request = store.put(value, id)
      request.onsuccess = () => resolve()
      request.onerror = () => {
        console.error(`Cache set failure ${id}`, request.error)
        resolve()
      }
    })
  }

  async setItems(items: { id: string }[]): Promise<void> {
    if (items.length === 0) {
      return
    }

    await init
    const transaction = getTransaction(this.name, 'readwrite')
    if (!transaction) {
      return
    }
    return new Promise(resolve => {
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => {
        console.error(`Cache setMany failure`, transaction.error)
        resolve()
      }
      const store = transaction.objectStore(this.name)
      items.forEach(item => store.put(item, item.id))
      transaction.commit()
    })
  }

  async deleteItems(ids: string[]): Promise<void> {
    if (ids.length === 0) {
      return
    }

    await init
    const transaction = getTransaction(this.name, 'readwrite')
    if (!transaction) {
      return
    }
    return new Promise(resolve => {
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => {
        console.error(`Cache deleteItems failure`, transaction.error)
        resolve()
      }
      const store = transaction.objectStore(this.name)
      ids.forEach(id => store.delete(id))
      transaction.commit()
    })
  }

  async deleteByType(type: string): Promise<void> {
    await init
    const transaction = getTransaction(this.name, 'readwrite')
    if (!transaction) {
      return
    }

    return new Promise(resolve => {
      const store = transaction.objectStore(this.name)
      const index = store.index('type')
      const range = IDBKeyRange.only(capitalize(type))
      const cursorRequest = index.openCursor(range)
      transaction.oncomplete = () => resolve()
      cursorRequest.onerror = transaction.onerror = () => {
        console.error(`Cache deleteByType failure`, transaction.error)
        resolve()
      }
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result
        if (cursor) {
          cursor.delete()
          cursor.continue()
        }
      }
    })
  }

  async getByType<T extends Entity>(type: string): Promise<EntityDict<T>> {
    await init
    const store = getStore(this.name, 'readonly')
    if (!store) {
      return {}
    }

    return new Promise(resolve => {
      const index = store.index('type')
      const request = index.getAll(capitalize(type))
      request.onsuccess = () => resolve(arrayToEntityDict(request.result as T[]))
      request.onerror = () => {
        console.error(`Cache getByType failure ${type}`, request.error)
        resolve({})
      }
    })
  }

  async clear(): Promise<void> {
    await init
    const store = getStore(this.name, 'readwrite')
    if (!store) {
      return
    }
    return new Promise(resolve => {
      const request = store.clear()
      request.onsuccess = () => resolve()
      request.onerror = () => {
        console.error(`Cache clear failure`, request.error)
        resolve()
      }
    })
  }
}

export const cache = new Cache(organizationStore)
export const adminCache = new Cache(adminStore)
